import { z } from 'zod';
import { apiFor } from '@/api/client';

/*
 * Contract for the winners endpoint — GET /reports/:id/winners.
 *
 * Unlike every other Monopoly surface, this is NOT a frozen pipeline payload:
 * the backend queries the prize service live, because a winner who completed a
 * collection a minute ago belongs on the page, and because the approval step
 * this grows into would be misleading if it lagged a pipeline run.
 *
 * The draws list carries the winner's NAME — the client approves it before
 * publication. The collections list does not: a card number identifies a
 * completion, and nothing there needs a person.
 */

export const COLLECTION_STATUSES = ['all', 'awaiting', 'reported'] as const;
export const LOTTERY_STATUSES = ['all', 'pending', 'approved'] as const;
export type WinnersTab = 'collections' | 'lottery';

const collectionWinnerSchema = z.object({
  id: z.number(),
  /** A completed colour collection, or a daily chance-card win. */
  kind: z.enum(['collection', 'lottery']).optional(),
  /** ISO timestamp the collection was completed. */
  date: z.string().nullable(),
  collectionCode: z.string().nullable(),
  /** The campaign's French name for the colour group ("Bleu foncé"). */
  collection: z.string(),
  color: z.string().nullable(),
  prize: z.string().nullable(),
  cardNumber: z.string().nullable(),
  /** The member id the client knows this customer by. Null when unresolved. */
  memberId: z.string().nullable(),
  /** The winner has supplied the details needed to hand the prize over. */
  dataProvided: z.boolean(),
  /** `awaiting` = not yet in a winner report; `reported` = sent for allocation. */
  status: z.enum(['awaiting', 'reported']),
});

const lotteryWinnerSchema = z.object({
  id: z.number(),
  date: z.string().nullable(),
  /** The winner's name, for the client to check and approve. Null until collected. */
  name: z.string().nullable(),
  draw: z.string(),
  prizeEur: z.number(),
  /** The member id of the account that held the winning chance card. */
  memberId: z.string().nullable(),
  claimedAt: z.string().nullable(),
  status: z.enum(['pending', 'approved']),
  approvedAt: z.string().nullable(),
  /**
   * A name proposed from this portal and awaiting manual entry in the admin
   * portal. It is NOT the prize service's name — that is `name` above.
   */
  proposedName: z.string().nullable().optional(),
  proposedAt: z.string().nullable().optional(),
});

const collectionSummarySchema = z.object({
  total: z.number(),
  winners: z.number(),
  reported: z.number(),
  awaiting: z.number(),
  dataProvided: z.number(),
  /** The split of `total` between the two kinds of win. */
  lottery: z.number().optional(),
  collections: z.number().optional(),
});

const lotterySummarySchema = z.object({
  total: z.number(),
  approved: z.number(),
  pending: z.number(),
  dailyPrizeEur: z.number(),
  finalPrizeEur: z.number(),
  finalDrawDate: z.string(),
});

const metaSchema = z.object({
  tab: z.string(),
  page: z.number(),
  limit: z.number(),
  total: z.number(),
  last_refreshed_at: z.string().nullable().optional(),
});

export const collectionWinnersEnvelope = z.object({
  data: z.array(collectionWinnerSchema),
  meta: metaSchema.extend({
    summary: collectionSummarySchema,
    /** Filter options, served by the API so the ten groups live in one place. */
    collections: z.array(z.object({ code: z.string(), name: z.string(), color: z.string() })),
  }),
});

export const lotteryWinnersEnvelope = z.object({
  data: z.array(lotteryWinnerSchema),
  meta: metaSchema.extend({ summary: lotterySummarySchema }),
});

export type CollectionWinner = z.infer<typeof collectionWinnerSchema>;
export type LotteryWinner = z.infer<typeof lotteryWinnerSchema>;
export type CollectionWinners = z.infer<typeof collectionWinnersEnvelope>;
export type LotteryWinners = z.infer<typeof lotteryWinnersEnvelope>;

export type WinnersQuery = {
  page: number;
  limit: number;
  status: string;
  collection: string | null;
  /** 'all' | 'collections' | 'lottery' — which kind of win to list. */
  kind?: string | null;
  /** Inclusive day bounds, YYYY-MM-DD, as the date inputs produce them. */
  from?: string | null;
  to?: string | null;
};

function search(tab: WinnersTab, q: WinnersQuery): string {
  const p = new URLSearchParams({ tab, page: String(q.page), limit: String(q.limit) });
  if (q.status !== 'all') p.set('status', q.status);
  if (q.collection) p.set('collection', q.collection);
  if (q.kind && q.kind !== 'all') p.set('kind', q.kind);
  if (q.from) p.set('from', q.from);
  if (q.to) p.set('to', q.to);
  return p.toString();
}

/**
 * Every collection winner matching the current filters, for the CSV export.
 *
 * The list endpoint is paged, so this walks the pages rather than asking for one
 * enormous response. `PAGE` is the server's own maximum, and `MAX_ROWS` is a
 * floor under runaway: an export is a file someone opens, not a data dump, and
 * a filter that matches everything should not try to stream the whole campaign
 * through a browser tab.
 */
const EXPORT_PAGE = 200;
export const EXPORT_MAX_ROWS = 20_000;

export async function getAllCollectionWinners(
  reportId: string,
  baseUrl: string,
  q: WinnersQuery,
  onProgress?: (loaded: number, total: number) => void,
): Promise<{ rows: CollectionWinner[]; truncated: boolean }> {
  const rows: CollectionWinner[] = [];
  let page = 1;
  let total = 0;
  for (;;) {
    const res = await getCollectionWinners(reportId, baseUrl, { ...q, page, limit: EXPORT_PAGE });
    total = res.meta.total;
    rows.push(...res.data);
    onProgress?.(rows.length, total);
    if (rows.length >= total || res.data.length === 0 || rows.length >= EXPORT_MAX_ROWS) break;
    page += 1;
  }
  return { rows, truncated: rows.length < total };
}

export async function getCollectionWinners(reportId: string, baseUrl: string, q: WinnersQuery): Promise<CollectionWinners> {
  const raw = await apiFor(baseUrl)
    .get(`reports/${reportId}/winners?${search('collections', q)}`)
    .json();
  return collectionWinnersEnvelope.parse(raw);
}

/**
 * Propose a name for a draw winner.
 *
 * This records the name IN THE PORTAL only. The prize service is not touched and
 * the winner stays "à valider" — an operator copies the name into the admin
 * portal by hand. The UI must not imply otherwise.
 */
export async function proposeWinnerName(reportId: string, baseUrl: string, winnerId: number, name: string): Promise<void> {
  await apiFor(baseUrl).post(`reports/${reportId}/winners/${winnerId}/name`, { json: { name } });
}

export async function getLotteryWinners(reportId: string, baseUrl: string, q: WinnersQuery): Promise<LotteryWinners> {
  const raw = await apiFor(baseUrl)
    .get(`reports/${reportId}/winners?${search('lottery', q)}`)
    .json();
  return lotteryWinnersEnvelope.parse(raw);
}
